import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { postJson } from '@/lib/post-json';

type AuthState =
  | { status: 'loading' }
  | { status: 'signedOut' }
  | { status: 'signedIn'; email: string };

interface AuthContextValue {
  state: AuthState;
  signedIn: (email: string) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/me')
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: 'signedOut' });
          return;
        }
        const { email } = (await res.json()) as { email: string };
        setState({ status: 'signedIn', email });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'signedOut' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A 401 anywhere (expired session, ops server restart) drops us back to
  // the login screen — use-api dispatches this event.
  useEffect(() => {
    const onUnauthorized = () => setState({ status: 'signedOut' });
    window.addEventListener('ops:unauthorized', onUnauthorized);
    return () => window.removeEventListener('ops:unauthorized', onUnauthorized);
  }, []);

  const signedIn = useCallback((email: string) => {
    setState({ status: 'signedIn', email });
  }, []);

  const signOut = useCallback(async () => {
    try {
      await postJson('/api/auth/logout');
    } finally {
      setState({ status: 'signedOut' });
    }
  }, []);

  return (
    <AuthContext.Provider value={{ state, signedIn, signOut }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth outside AuthProvider');
  return value;
}
